import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { WorkflowDataService } from '../../services/workflow-data.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-quotation-status-dialog',
  templateUrl: './quotation-status-dialog.component.html',
  styleUrls: ['./quotation-status-dialog.component.scss'],
})
export class QuotationStatusDialogComponent implements OnInit {
  quotationNo: string = '';
  status: string = '';
  statusId: string | undefined;
  progressValue: number = 0;
  activeIndex: number = 0;
  loading: boolean = false;

  // Enhanced status options with better icons and descriptions
  availableStatuses: { label: string; value: string; icon: string; description: string }[] = [
    { label: 'Order Release', value: 'Production Order Release', icon: 'pi pi-file-export', description: 'Production order has been created and released' },
    { label: 'Planning', value: 'Planning', icon: 'pi pi-calendar-plus', description: 'Production schedule is being planned' },
    { label: 'Shearing & Punching', value: 'Shearing & Punching', icon: 'pi pi-cut', description: 'Metal cutting and hole punching in progress' },
    { label: 'Bending', value: 'Bending', icon: 'pi pi-angle-double-right', description: 'Metal bending and forming stage' },
    { label: 'Welding', value: 'Welding', icon: 'pi pi-bolt', description: 'Components being welded together' },
    { label: 'Powder Coating', value: 'Powder Coating', icon: 'pi pi-palette', description: 'Surface treatment and painting' },
    { label: 'Assembly', value: 'Assembling/Rubber Beading', icon: 'pi pi-cog', description: 'Final assembly and rubber beading' },
    { label: 'Packing', value: 'Packing', icon: 'pi pi-box', description: 'Product packaging for shipment' },
    { label: 'Complete', value: 'Complete', icon: 'pi pi-check-circle', description: 'Order completed and ready for delivery' },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private workflowDataService: WorkflowDataService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    const workflowData = this.config.data?.workflowData;
    if (workflowData) {
      this.quotationNo = workflowData.quotationNo || '';
      this.status = workflowData.status || '';
      this.statusId = workflowData.statusId;
      this.updateProgressAndIndex();
    }
  }

  saveStatus(): void {
    if (!this.quotationNo || !this.status) {
      this.messageService.add({
        severity: 'error',
        summary: 'Required Fields',
        detail: 'Please select a status to update',
        life: 3000,
      });
      return;
    }

    this.loading = true;
    this.workflowDataService
      .saveQuotationStatus(this.quotationNo, this.status, this.statusId)
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Status Updated',
            detail: `Work order ${this.quotationNo} is now ${this.getStatusLabel(this.status)}`,
            life: 3000,
          });
          this.ref.close({
            status: this.status,
            statusId: response.data.id,
            statusLabel: this.getStatusLabel(this.status)
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: `Could not update status for ${this.quotationNo}`,
            life: 3000,
          });
          console.error('Save status error:', err);
        },
        complete: () => this.loading = false
      });
  }

  cancel(): void {
    this.ref.close();
  }

  onStatusChange(): void {
    this.updateProgressAndIndex();
  }

  getStatusLabel(statusValue: string): string {
    const status = this.availableStatuses.find(s => s.value === statusValue);
    return status ? status.label : statusValue;
  }

  getStatusDescription(statusValue: string): string {
    const status = this.availableStatuses.find(s => s.value === statusValue);
    return status ? status.description : '';
  }

  private updateProgressAndIndex(): void {
    const statusIndex = this.availableStatuses.findIndex((s) => s.value === this.status);
    this.activeIndex = statusIndex >= 0 ? statusIndex : 0;
    this.progressValue = statusIndex >= 0 ? ((statusIndex + 1) / this.availableStatuses.length) * 100 : 0;
  }
}